import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../api/api-context';
import type { RepertoireLine } from '../api/types';
import { buildNodeRoute } from '../state/route';
import { recentBuilderRoute } from '../state/recent-builder';
import { LineContextMenu, RenameLineDialog, type LineMenuState } from './LineRename';
import { PracticePanel } from './PracticeSummary';

interface ConfirmLineDeleteProps {
  line: RepertoireLine;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Accessible confirm modal for deleting a line (§4 deleteLine). */
function ConfirmLineDelete({
  line,
  busy,
  error,
  onConfirm,
  onCancel,
}: ConfirmLineDeleteProps): JSX.Element {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    function onKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.key === 'Escape' && !busy) onCancel();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel, busy]);

  return (
    <div className="modal-backdrop" data-testid="confirm-line-delete">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-line-delete-title"
        className="modal"
      >
        <h2 id="confirm-line-delete-title">Delete “{line.name ?? 'Unnamed variation'}”?</h2>
        <p className="muted">
          Deletes this line from the repertoire. Nodes shared with other lines are kept.
        </p>
        {error !== null && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        <div className="add-move-actions">
          <button type="button" className="danger" disabled={busy} onClick={onConfirm}>
            {busy ? 'Deleting…' : 'Delete line'}
          </button>
          <button
            ref={cancelRef}
            type="button"
            className="secondary"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function RepertoireHome(): JSX.Element {
  const { repertoireId } = useParams<{ repertoireId: string }>();
  const api = useApi();
  const queryClient = useQueryClient();
  const [toDelete, setToDelete] = useState<RepertoireLine | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [lineMenu, setLineMenu] = useState<LineMenuState | null>(null);
  const [lineToRename, setLineToRename] = useState<RepertoireLine | null>(null);
  const [lineRenameNotice, setLineRenameNotice] = useState<string | null>(null);

  const repertoireQuery = useQuery({
    queryKey: ['repertoire', repertoireId],
    queryFn: () => api.getRepertoire(repertoireId!),
    enabled: !!repertoireId,
  });
  const linesQuery = useQuery({
    queryKey: ['lines', repertoireId],
    queryFn: () => api.listLines(repertoireId!),
    enabled: !!repertoireId,
  });

  const etag = repertoireQuery.data?.etag ?? null;

  async function onRename(): Promise<void> {
    if (!repertoireId || !etag || !newName.trim()) return;
    setRenameBusy(true);
    setRenameError(null);
    try {
      await api.renameRepertoire(repertoireId, newName.trim(), etag);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['repertoire', repertoireId] }),
        queryClient.invalidateQueries({ queryKey: ['repertoires'] }),
      ]);
      setRenaming(false);
    } catch (cause) {
      setRenameError(cause instanceof Error ? cause.message : 'Could not rename this repertoire.');
    } finally {
      setRenameBusy(false);
    }
  }

  async function onDeleteLine(): Promise<void> {
    if (toDelete === null || repertoireId === undefined) return;
    setDeleteError(null);
    if (etag === null) {
      setDeleteError('Could not load the current repertoire version. Refresh and try again.');
      return;
    }
    setDeleting(true);
    try {
      await api.deleteLine(repertoireId, toDelete.lineId, etag);
      setToDelete(null);
      await queryClient.invalidateQueries({
        queryKey: ['lines', repertoireId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['repertoire', repertoireId],
      });
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete the line.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <main className="page">
      <p className="studio-eyebrow">YOUR OPENING PLAYBOOK</p>
      <h1>{repertoireQuery.data?.data.name ?? 'Repertoire'}</h1>
      {!renaming ? (
        <button type="button" className="secondary" onClick={() => {
          setNewName(repertoireQuery.data?.data.name ?? '');
          setRenaming(true);
        }}>Rename repertoire</button>
      ) : (
        <form className="repertoire-rename" onSubmit={(event) => { event.preventDefault(); void onRename(); }}>
          <label htmlFor="repertoire-new-name">Repertoire name</label>
          <input id="repertoire-new-name" value={newName} maxLength={160} onChange={(event) => setNewName(event.target.value)} />
          <button type="submit" disabled={renameBusy || !newName.trim()}>Save name</button>
          <button type="button" className="secondary" disabled={renameBusy} onClick={() => setRenaming(false)}>Cancel</button>
          {renameError && <p role="alert">{renameError}</p>}
        </form>
      )}
      <p className="muted">
        Organize your variations, revisit the moves, and keep your preparation focused.
      </p>
      <Link to="/" className="muted">
        ← All repertoires
      </Link>
      {repertoireId && <PracticePanel repertoireId={repertoireId} />}
      <div className="library-toolbar">
        <Link to={`/games?repertoire=${repertoireId}`}>Compare imported games →</Link>
        <label>
          Find a variation
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or moves"
          />
        </label>
        {repertoireQuery.data && (
          <Link
            className="studio-primary"
            to={recentBuilderRoute(repertoireId!, buildNodeRoute({
              repertoireId: repertoireId!,
              zobristHash: repertoireQuery.data.data.defaultRoot.position.zobristHash,
              positionId: repertoireQuery.data.data.defaultRoot.position.positionId,
              path: null,
            }))}
          >
            Open line builder →
          </Link>
        )}
      </div>
      <h2>Your variations</h2>
      {lineRenameNotice && <p role="status">{lineRenameNotice}</p>}
      {(linesQuery.isError || repertoireQuery.isError) && (
        <p role="alert">
          Could not load your repertoire.{' '}
          <button
            onClick={() => {
              void linesQuery.refetch();
              void repertoireQuery.refetch();
            }}
          >
            Try again
          </button>
        </p>
      )}
      {linesQuery.isLoading && <p role="status">Loading lines…</p>}
      {linesQuery.data && linesQuery.data.items.length === 0 && (
        <p className="muted">
          Your first line will appear here after you play a move in the line builder.
        </p>
      )}
      {linesQuery.data && linesQuery.data.items.length > 0 && (
        <ul className="repertoire-list">
          {linesQuery.data.items
            .filter((line) =>
              `${line.name ?? ''} ${line.movesSan?.join(' ') ?? ''}`
                .toLowerCase()
                .includes(search.toLowerCase()),
            )
            .map((line) => {
              const root = line.nodes[0];
              return (
                <li
                  key={line.lineId}
                  className="repertoire-row"
                  onContextMenu={(event) => {
                    event.preventDefault();
                    setLineMenu({
                      line,
                      x: Math.max(8, Math.min(event.clientX, window.innerWidth - 220)),
                      y: Math.max(8, Math.min(event.clientY, window.innerHeight - 70)),
                    });
                  }}
                >
                  <Link
                    onKeyDown={(event) => {
                      if (event.key !== 'ContextMenu' && !(event.shiftKey && event.key === 'F10')) return;
                      event.preventDefault();
                      const bounds = event.currentTarget.getBoundingClientRect();
                      setLineMenu({ line, x: bounds.left, y: bounds.bottom });
                    }}
                    to={buildNodeRoute({
                      repertoireId: repertoireId!,
                      zobristHash: root.zobristHash,
                      positionId: root.positionId,
                      path: { lineId: line.lineId, ply: 0 },
                    })}
                  >
                    {line.name ?? 'Unnamed variation'}
                    <span className="variation-preview muted">
                      {line.movesSan?.slice(0, 12).join(' · ') || 'Open variation'}
                      {line.movesUci.length > 12 ? ' …' : ''}
                    </span>
                  </Link>
                  <button
                    type="button"
                    className="secondary line-rename-button"
                    onClick={() => setLineToRename(line)}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    className="row-delete"
                    onClick={() => {
                      setDeleteError(null);
                      setToDelete(line);
                    }}
                  >
                    Delete
                  </button>
                </li>
              );
            })}
        </ul>
      )}
      {lineMenu && (
        <LineContextMenu
          menu={lineMenu}
          onClose={() => setLineMenu(null)}
          onRename={(target) => { setLineMenu(null); setLineToRename(target); }}
        />
      )}
      {lineToRename && repertoireId && (
        <RenameLineDialog
          line={lineToRename}
          repertoireId={repertoireId}
          onClose={() => setLineToRename(null)}
          onRenamed={(name) => setLineRenameNotice(`${name} renamed.`)}
        />
      )}
      {toDelete !== null && (
        <ConfirmLineDelete
          line={toDelete}
          busy={deleting}
          error={deleteError}
          onConfirm={() => void onDeleteLine()}
          onCancel={() => {
            setToDelete(null);
            setDeleteError(null);
          }}
        />
      )}
    </main>
  );
}
